import { BalanceData, LevelData, bossDelayOf, bossHpMultOf, stagePowerOf } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { PLAIN_RUN, ResolvedModifiers } from "./modifiers";

export interface SpawnEntry {
  readonly tick: number;
  readonly archetype: number;
  readonly hpMult: Fix64;
  readonly isBoss: boolean;
  /** -1 = use the archetype reward. */
  readonly pointsOverride: number;
  /** Elite affix index (DESIGN §5.1); absent or −1 = a plain spawn. */
  readonly affix?: number;
  /** Multiplier on the archetype's speed (its group's `speedMult`); absent = 1. */
  readonly speedMult?: Fix64;
  /** Immunity bits (M36, `Immunity`, sim/immunity.ts); absent or 0 = none. */
  readonly immunity?: number;
  /** The stage's rare spawn (M36, sim/rares.ts): this entry IS the rare; absent = an ordinary spawn. */
  readonly rare?: boolean;
  /** The stage's Starfall Wyrm (M40, sim/wyrm.ts): its HP is set from the board when it appears; absent = not the Wyrm. */
  readonly wyrm?: boolean;
  /** The run's wild pet, engaged (Pets v2, sim/wildPet.ts): this entry IS it; absent = not the wild pet. */
  readonly wildPet?: boolean;
}

/** Whether a run plays the campaign's own waves (LevelData.hpMult applies): not Iron, Endless, Daily Road, an event run or a challenge code. */
export function campaignRun(mods: ResolvedModifiers): boolean {
  return mods.iron === null && mods.endless === null && mods.daily === null && mods.event === null && mods.challenge === null;
}

/**
 * Builds the per-stage spawn schedule from the level's groups and the power tables (§3.2, §5), then marks the elites
 * (§5.1) and applies the run's HP modifier. A plain run (PLAIN_RUN) builds exactly the schedule it always did.
 */
export function buildWaveSchedule(balance: BalanceData, level: LevelData, levelNumber: number, stage: number, stages: number, startDelay: number, mods: ResolvedModifiers = PLAIN_RUN): SpawnEntry[] {
  const out: SpawnEntry[] = [];
  let power = balance.tables.levelBaseAt(levelNumber).mul(stagePowerOf(balance, level, stage));
  // M56: the campaign's difficulty multiplier (LevelData.hpMult) on a campaign run — a plain level, a farm replay or a
  // Weekly Trial on it; the other modes set their own power.
  if (!level.hpMult.eq(Fix64.One) && campaignRun(mods)) power = power.mul(level.hpMult);
  if (!mods.enemyHpMult.eq(Fix64.One)) power = power.mul(mods.enemyHpMult);
  let lastTick = startDelay;
  // MP-21 Boss Week tiers: every group's count × countMult, rounded up (absent on every other run: counts unchanged).
  const countMult = mods.event !== null && !mods.event.countMult.eq(Fix64.One) ? mods.event.countMult : null;
  for (const g of level.groups) {
    const count = countMult === null ? g.countAtStage(stage) : countMult.mulInt(g.countAtStage(stage)).ceilToInt();
    for (let r = 0; r < Math.max(1, g.repeats); r++) {
      for (let i = 0; i < count; i++) {
        const tick = startDelay + g.delayTicks + r * g.repeatGapTicks + i * g.intervalTicks;
        const entry: SpawnEntry = { tick, archetype: g.enemyIndex, hpMult: power, isBoss: false, pointsOverride: -1 };
        out.push(g.speedMult.eq(Fix64.One) ? entry : { ...entry, speedMult: g.speedMult });
        if (tick > lastTick) lastTick = tick;
      }
    }
  }
  if (stage === stages) {
    out.push({ tick: lastTick + bossDelayOf(balance, level), archetype: level.bossEnemyIndex, hpMult: power.mul(bossHpMultOf(balance, level)), isBoss: true, pointsOverride: balance.economy.bossPoints });
  }
  // Stable insertion sort by tick keeps group order for equal ticks.
  for (let i = 1; i < out.length; i++) {
    const key = out[i];
    let j = i - 1;
    while (j >= 0 && out[j].tick > key.tick) { out[j + 1] = out[j]; j--; }
    out[j + 1] = key;
  }
  return mods.elites.length === 0 ? out : markElites(balance, out, stage, mods);
}

/**
 * Every rule marks every `every`-th ordinary spawn of the stage (counted in schedule order, bosses skipped) from its
 * `fromStage`; a spawn two rules pick wears the first rule's affix. An archetype that never wears one (`noAffix`, the
 * Spellward) is skipped and not counted, so a level without one marks exactly the spawns it always did. Pure: the
 * preview and the run mark the same ones.
 */
function markElites(balance: BalanceData, entries: SpawnEntry[], stage: number, mods: ResolvedModifiers): SpawnEntry[] {
  let ordinal = 0;
  return entries.map((e) => {
    if (e.isBoss || balance.enemies[e.archetype].abilities.noAffix) return e;
    ordinal++;
    for (const r of mods.elites) if (stage >= r.fromStage && ordinal % r.every === 0) return { ...e, affix: r.affix };
    return e;
  });
}
